import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { Box, Center, HStack, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import useCheatCode from '@hooks/useCheatCode';
import { useStableIds } from '@hooks/useStableIds';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { selectAllOngoingNotifications } from '@redux/data/data.selectors';
import { updateSelectedNotificationId } from '@redux/global/global.reducer';
import { selectSelectedNotificationId } from '@redux/global/global.selector';
import { useArchiveAllMutation } from '@services/data/notification.api';
import {
  filterNotifications,
  isLadNotification,
  isLadTechnicalNotification,
  isPerimeterNotification,
  isPerimeterTechnicalNotification,
  sortNotifications,
} from '@utils/data/notification.utils';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationFilterEnum, NotificationSortEnum } from '@/types/data/data.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import Panel from '../Panel';
import NotificationDetails from './detail/NotificationDetails';
import LiveNotificationList from './LiveNotificationList';
import NotificationHeader from './NotificationHeader';

export default function NotificationsPanel() {
  const { formatMessage } = useIntl();
  const selectedNotification = useAppSelector(selectSelectedNotificationId);
  const dispatchUpdateSelectedNotification = useWithDispatch(updateSelectedNotificationId);
  const [selectedFilters, setSelectedFilters] = useState<NotificationFilterEnum[]>(
    Object.values(NotificationFilterEnum),
  );
  const [selectedSort, setSelectedSort] = useState<NotificationSortEnum>(NotificationSortEnum.START_TIME);
  const notifications = useAppSelector(selectAllOngoingNotifications);

  const [archiveAll] = useArchiveAllMutation();
  useCheatCode('archive all notifications', ['z', 'z', 'z', 's'], () => archiveAll(false));
  useCheatCode('archive all notifications', ['z', 'z', 'z', 'f'], () => archiveAll(true));

  useEffect(() => {
    if (notifications.every((notification) => notification.id !== selectedNotification)) {
      dispatchUpdateSelectedNotification(null);
    }
  }, [dispatchUpdateSelectedNotification, notifications, selectedNotification]);

  const notificationCount = useMemo(() => {
    const counts = {
      [NotificationFilterEnum.LAD]: 0,
      [NotificationFilterEnum.PERIMETER]: 0,
      [NotificationFilterEnum.LAD_TECHNICAL]: 0,
      [NotificationFilterEnum.PERIMETER_TECHNICAL]: 0,
    };

    for (const n of notifications) {
      if (isLadNotification(n)) counts[NotificationFilterEnum.LAD]++;
      else if (isPerimeterNotification(n)) counts[NotificationFilterEnum.PERIMETER]++;
      else if (isLadTechnicalNotification(n)) counts[NotificationFilterEnum.LAD_TECHNICAL]++;
      else if (isPerimeterTechnicalNotification(n)) counts[NotificationFilterEnum.PERIMETER_TECHNICAL]++;
    }

    return counts;
  }, [notifications]);

  const filteredNotifications = sortNotifications(filterNotifications(notifications, selectedFilters), selectedSort);
  const filteredNotificationIds = useStableIds(filteredNotifications.map((notification) => notification.id));

  return (
    <Panel
      isEmpty={false}
      gap={0}
      header={<CockpitPanelHeader label={formatMessage({ id: 'components.notification.title' })} />}
    >
      <HStack gap={0.25} width="100%" height="100%">
        <VStack gap={0.25} width="100%" height="100%">
          <NotificationHeader
            notificationCount={notificationCount}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            canSort={notifications.length > 0}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
          <Box height="100%" width="100%" flex={1} minHeight={0} backgroundColor="neutral.700">
            {filteredNotificationIds.length > 0 ? (
              <LiveNotificationList notificationIds={filteredNotificationIds} />
            ) : (
              <Center width="100%" height="100%" backgroundColor="neutral.black">
                <VStack gap={3}>
                  <EmptyPanelDroneIcon />
                  <Text
                    textStyle="lg"
                    fontFamily="space"
                    fontFeatureSettings="'liga' off"
                    color="neutral.200"
                    maxWidth="230px"
                    textAlign="center"
                    userSelect="none"
                  >
                    <FormattedMessage id="components.notification.empty" />
                  </Text>
                </VStack>
              </Center>
            )}
          </Box>
        </VStack>
        <NotificationDetails selectedNotification={selectedNotification} />
      </HStack>
    </Panel>
  );
}
